import { useQuery } from '@tanstack/react-query';

import { getPlusMemberLifetimeSavings } from 'api/jalapeno';

import useAccountQuery from './useAccountQuery';

export const PLUS_MEMBERSHIP_LIFETIME_SAVINGS_QUERY_KEY = 'plus_membership_lifetime_savings';

/**
 * Custom hook to fetch a Plus member's lifetime savings data
 * @param {boolean} enabled - Flag to enable/disable the query, defaults to true
 *
 * @returns {Object} Query object containing:
 * - data: The member's lifetime savings information including totalSavings, totalPlusItemSavings, and totalPlusShippingSavings
 * - isLoading: Boolean indicating if the query is in progress
 * - error: Any error that occurred during the query
 * - refetch: Function to manually refetch the data
 * - and other React Query properties
 *
 * @example
 * const { data: savingsData, isLoading } = usePlusMembershipLifetimeSavingsQuery();
 * // savingsData.totalSavings - total amount saved as a Plus member
 * // savingsData.totalPlusItemSavings - savings from Plus member discounts on items
 * // savingsData.totalPlusShippingSavings - savings from Plus member shipping benefits
 */

export const usePlusMembershipLifetimeSavingsQuery = (enabled = true) => {
    const { data: account } = useAccountQuery();

    const query = useQuery({
        queryKey: [PLUS_MEMBERSHIP_LIFETIME_SAVINGS_QUERY_KEY, account?.customerID],
        queryFn: async () => {
            const response = await getPlusMemberLifetimeSavings({ customerID: account?.customerID });
            return response.data?.lifetimeSavings ?? null;
        },
        enabled: enabled && !!account?.customerID,
    });

    return query;
};
